/**
 * Texture3DPainter.js
 * 3D Texture Editor mit Three.js
 *
 * Unterstuetzt verschiedene Geometrie-Typen:
 *   'block' — Wuerfel (Bloecke wie stone, dirt, etc.)
 *   'item'  — Flaches Sprite-Quad (Items wie sword, apple, etc.)
 *   'cross' — Kreuzform (Pflanzen wie Blumen, Setzlinge, etc.)
 *
 * Direktes Malen via Raycasting.
 * Rechtsklick + Ziehen = Drehen, Scroll = Zoom.
 */

import React, {
  useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle,
} from 'react';
import * as THREE from 'three';

// ── Geometrie-Builder ──────────────────────────────────────────────────────

/** Block: einfacher Wuerfel (6 Seiten, gleiche Textur) */
function buildBlockGeometry() {
  return new THREE.BoxGeometry(0.8, 0.8, 0.8);
}

/**
 * Item: flaches Sprite-Quad wie in Minecraft 3rd-Person / Item-Frame.
 * Leicht geneigt wie bei der Bodenansicht.
 * Zwei Seiten (DoubleSide), damit man es von hinten auch sieht.
 */
function buildItemGeometry() {
  const geo = new THREE.PlaneGeometry(0.8, 0.8);
  return geo;
}

/**
 * Cross/Pflanze: Zwei sich kreuzende Planes (X-Form),
 * wie Blumen, Setzlinge, Gras etc. in Minecraft.
 */
function buildCrossGeometry() {
  const s = 0.55; // Halbe Seitenlaenge
  const positions = [];
  const normals = [];
  const uvs = [];
  const indices = [];

  // Plane 1: entlang der X-Z-Diagonale (von -s,-s nach +s,+s)
  // Vorderseite
  positions.push(-s, -s, -s,  s, -s, s,  s, s, s,  -s, s, -s);
  normals.push(  -0.707, 0, 0.707,  -0.707, 0, 0.707,  -0.707, 0, 0.707,  -0.707, 0, 0.707);
  uvs.push(0, 0,  1, 0,  1, 1,  0, 1);
  indices.push(0, 1, 2,  0, 2, 3);
  // Rueckseite
  positions.push(s, -s, s,  -s, -s, -s,  -s, s, -s,  s, s, s);
  normals.push(  0.707, 0, -0.707,  0.707, 0, -0.707,  0.707, 0, -0.707,  0.707, 0, -0.707);
  uvs.push(0, 0,  1, 0,  1, 1,  0, 1);
  indices.push(4, 5, 6,  4, 6, 7);

  // Plane 2: entlang der anderen Diagonale (von +s,-s nach -s,+s)
  // Vorderseite
  positions.push(s, -s, -s,  -s, -s, s,  -s, s, s,  s, s, -s);
  normals.push(  0.707, 0, 0.707,  0.707, 0, 0.707,  0.707, 0, 0.707,  0.707, 0, 0.707);
  uvs.push(0, 0,  1, 0,  1, 1,  0, 1);
  indices.push(8, 9, 10,  8, 10, 11);
  // Rueckseite
  positions.push(-s, -s, s,  s, -s, -s,  s, s, -s,  -s, s, s);
  normals.push(  -0.707, 0, -0.707,  -0.707, 0, -0.707,  -0.707, 0, -0.707,  -0.707, 0, -0.707);
  uvs.push(0, 0,  1, 0,  1, 1,  0, 1);
  indices.push(12, 13, 14,  12, 14, 15);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  return geo;
}

function buildGeometry(type) {
  switch (type) {
    case 'item':  return buildItemGeometry();
    case 'cross': return buildCrossGeometry();
    case 'block':
    default:      return buildBlockGeometry();
  }
}

// ── Hauptkomponente ────────────────────────────────────────────────────────
// Face-Index-Mapping: THREE.BoxGeometry faceIndex/2 -> faceKey
const FACE_KEYS = ['right', 'left', 'top', 'bottom', 'front', 'back'];

const Texture3DPainter = forwardRef(({
  textureDataUrl,             // Single-Textur (string dataUrl) — oder null wenn faceTextures
  faceTextures,               // Multi-Textur: { right, left, top, bottom, front, back } — dataUrls
  geometryType = 'block',     // 'block' | 'item' | 'cross'
  tool = 'pencil',
  color = [0, 0, 0, 255],
  brushSize = 1,
  onPaint,        // fn(ImageData, faceKey?) — Live-Sync, kein History-Push
  onStrokeEnd,    // fn(ImageData, faceKey?) — nur bei pointerUp, fuer History
  onColorPick,
  canvasSize = 16,
  isAnimated = false,         // true if texture is an animation strip
  animFrameCount = 1,         // number of frames in the strip
}, ref) => {
  const mountRef    = useRef(null);
  const sceneRef    = useRef(null);
  const camRef      = useRef(null);
  const rendRef     = useRef(null);
  const groupRef    = useRef(null);
  // Single-Textur Mode
  const texCanvasRef = useRef(null);
  const texCtxRef   = useRef(null);
  const threeTexRef  = useRef(null);
  // Multi-Textur Mode: { right: {canvas, ctx, tex}, left: {...}, ... }
  const facesRef    = useRef({});
  const rafRef      = useRef(null);

  const orbitRef    = useRef({ active: false, lastX: 0, lastY: 0 });
  const rotRef      = useRef({ y: 0.4, x: -0.3 });
  const zoomRef     = useRef(1.5);
  const paintRef    = useRef({ active: false, lastFaceKey: null });
  const didPaintRef = useRef(false);

  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef     = useRef(new THREE.Vector2());

  // Animation playback state
  const [animPlaying, setAnimPlaying] = useState(false);
  const [animFps, setAnimFps] = useState(10);
  const animFrameRef = useRef(0);
  const animStripImgRef = useRef(null);  // stores the full strip Image
  const animIntervalRef = useRef(null);

  // ── Init scene (einmal) ──────────────────────────────────────────────────
  useEffect(() => {
    if (!mountRef.current) return;

    const w = mountRef.current.clientWidth  || 400;
    const h = mountRef.current.clientHeight || 400;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    const cam = new THREE.PerspectiveCamera(45, w / h, 0.01, 100);
    cam.position.z = zoomRef.current;
    camRef.current = cam;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mountRef.current.appendChild(renderer.domElement);
    rendRef.current = renderer;

    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(3, 5, 3);
    scene.add(dir);
    const dir2 = new THREE.DirectionalLight(0x8899ff, 0.3);
    dir2.position.set(-3, -2, -3);
    scene.add(dir2);

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      renderer.render(scene, cam);
    };
    tick();

    const onResize = () => {
      if (!mountRef.current) return;
      const nw = mountRef.current.clientWidth;
      const nh = mountRef.current.clientHeight;
      cam.aspect = nw / nh;
      cam.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, []);

  // ── Textur laden + Geometrie bauen ───────────────────────────────────────
  useEffect(() => {
    if (!sceneRef.current) return;
    const isMulti = faceTextures && typeof faceTextures === 'object';
    const hasSingle = !!textureDataUrl;
    if (!isMulti && !hasSingle) return;

    // Alte Gruppe aufraemen
    function clearGroup() {
      if (groupRef.current) {
        sceneRef.current.remove(groupRef.current);
        groupRef.current.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
            else child.material.dispose();
          }
        });
        groupRef.current = null;
      }
    }

    function makeMat(tex) {
      return new THREE.MeshStandardMaterial({
        map: tex, roughness: 0.9, metalness: 0.0,
        transparent: true, alphaTest: 0.01, side: THREE.FrontSide,
      });
    }

    function buildGroup(materials) {
      const group = new THREE.Group();
      if (geometryType === 'cross') {
        const mat = Array.isArray(materials) ? materials[4] : materials;
        group.add(new THREE.Mesh(buildCrossGeometry(), mat));
      } else if (geometryType === 'item') {
        const mat = Array.isArray(materials) ? materials[4] : materials;
        const mesh = new THREE.Mesh(buildItemGeometry(), mat);
        mesh.rotation.x = -0.15;
        group.add(mesh);
      } else {
        const mat = Array.isArray(materials) ? materials : materials;
        group.add(new THREE.Mesh(buildBlockGeometry(), mat));
      }
      group.rotation.y = rotRef.current.y;
      group.rotation.x = rotRef.current.x;
      sceneRef.current.add(group);
      groupRef.current = group;
    }

    if (isMulti) {
      // Multi-Textur: Faces die dieselbe DataURL teilen, bekommen DASSELBE Canvas/Ctx/Tex-Objekt.
      // Dadurch ist paintAt('top') automatisch auch auf 'bottom' sichtbar (wenn sie die gleiche Textur haben).
      const newFaces = {};
      const faceOrder = ['right', 'left', 'top', 'bottom', 'front', 'back'];
      const matArray = new Array(6).fill(null);

      // Schritt 1: Einzigartige DataURLs bestimmen und ein Shared-Objekt pro URL anlegen
      const urlToShared = {}; // dataUrl (oder '__empty__') -> { canvas, ctx, tex, mat }

      function makeShared(dataUrl, onReady) {
        const key = dataUrl || '__empty__';
        if (urlToShared[key]) {
          onReady(urlToShared[key]);
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = canvasSize; canvas.height = canvasSize;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const tex = new THREE.CanvasTexture(canvas);
        tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter;
        const mat = makeMat(tex);
        const shared = { canvas, ctx, tex, mat };
        urlToShared[key] = shared;

        if (!dataUrl) {
          onReady(shared);
          return;
        }
        const loader = new THREE.TextureLoader();
        loader.load(dataUrl, (loadedTex) => {
          ctx.imageSmoothingEnabled = false;
          const srcW = loadedTex.image.width;
          const srcH = loadedTex.image.height;
          if (srcH > srcW) {
            // Animation strip — extract first frame
            ctx.drawImage(loadedTex.image, 0, 0, srcW, srcW, 0, 0, canvasSize, canvasSize);
          } else {
            ctx.drawImage(loadedTex.image, 0, 0, canvasSize, canvasSize);
          }
          tex.needsUpdate = true;
          onReady(shared);
        });
      }

      // Schritt 2: Alle 6 Faces laden (shared wo moeglich)
      let loaded = 0;
      faceOrder.forEach((faceKey, i) => {
        const dataUrl = faceTextures[faceKey];
        makeShared(dataUrl, (shared) => {
          newFaces[faceKey] = shared;
          matArray[i] = shared.mat;
          loaded++;
          if (loaded === faceOrder.length) {
            clearGroup();
            facesRef.current = newFaces;
            buildGroup(matArray);
          }
        });
      });
    } else {
      // Single-Textur (bisheriges Verhalten)
      const loader = new THREE.TextureLoader();
      loader.load(textureDataUrl, (loadedTex) => {
        const canvas = document.createElement('canvas');
        canvas.width = canvasSize; canvas.height = canvasSize;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.imageSmoothingEnabled = false;
        // If the source image is taller than wide (animation strip), only draw the
        // first frame (top canvasSize x canvasSize region) instead of squishing.
        const srcW = loadedTex.image.width;
        const srcH = loadedTex.image.height;
        if (srcH > srcW) {
          // Animation strip — extract first frame
          ctx.drawImage(loadedTex.image, 0, 0, srcW, srcW, 0, 0, canvasSize, canvasSize);
        } else {
          ctx.drawImage(loadedTex.image, 0, 0, canvasSize, canvasSize);
        }
        texCanvasRef.current = canvas;
        texCtxRef.current = ctx;
        const tex = new THREE.CanvasTexture(canvas);
        tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter;
        threeTexRef.current = tex;

        const useSide = geometryType === 'block' ? THREE.FrontSide : THREE.DoubleSide;
        const mat = new THREE.MeshStandardMaterial({
          map: tex, roughness: 0.9, metalness: 0.0,
          transparent: true, alphaTest: 0.01, side: useSide,
        });
        clearGroup();
        const group = new THREE.Group();
        if (geometryType === 'cross') {
          group.add(new THREE.Mesh(buildCrossGeometry(), mat));
        } else if (geometryType === 'item') {
          const mesh = new THREE.Mesh(buildItemGeometry(), mat);
          mesh.rotation.x = -0.15;
          group.add(mesh);
        } else {
          group.add(new THREE.Mesh(buildBlockGeometry(), mat));
        }
        group.rotation.y = rotRef.current.y;
        group.rotation.x = rotRef.current.x;
        sceneRef.current.add(group);
        groupRef.current = group;
      });
    }
  }, [textureDataUrl, faceTextures, canvasSize, geometryType]); // eslint-disable-line

  // ── Animation strip: preload full image for frame cycling ───────────────
  useEffect(() => {
    if (!isAnimated || !textureDataUrl || animFrameCount <= 1) {
      animStripImgRef.current = null;
      return;
    }
    const img = new Image();
    img.onload = () => { animStripImgRef.current = img; };
    img.src = textureDataUrl;
  }, [isAnimated, textureDataUrl, animFrameCount]);

  // ── Animation playback interval ────────────────────────────────────────
  useEffect(() => {
    if (animIntervalRef.current) {
      clearInterval(animIntervalRef.current);
      animIntervalRef.current = null;
    }
    if (!animPlaying || !isAnimated || animFrameCount <= 1) return;

    animFrameRef.current = 0;
    animIntervalRef.current = setInterval(() => {
      const img = animStripImgRef.current;
      const c = texCanvasRef.current;
      const ctx = texCtxRef.current;
      if (!img || !c || !ctx) return;

      const frame = animFrameRef.current % animFrameCount;
      const srcY = frame * canvasSize;
      ctx.clearRect(0, 0, canvasSize, canvasSize);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, srcY, canvasSize, canvasSize, 0, 0, canvasSize, canvasSize);
      if (threeTexRef.current) threeTexRef.current.needsUpdate = true;
      animFrameRef.current = frame + 1;
    }, 1000 / animFps);

    return () => {
      if (animIntervalRef.current) {
        clearInterval(animIntervalRef.current);
        animIntervalRef.current = null;
      }
    };
  }, [animPlaying, isAnimated, animFrameCount, animFps, canvasSize]);

  // Stop animation when user starts painting
  useEffect(() => {
    if (animPlaying && paintRef.current?.active) {
      setAnimPlaying(false);
    }
  });

  // ── Malen ────────────────────────────────────────────────────────────────
  // Gibt Canvas+Ctx fuer die gegebene faceKey (multi) oder den single-canvas zurueck
  const getCanvas = useCallback((faceKey) => {
    if (faceKey && facesRef.current[faceKey]) return facesRef.current[faceKey];
    return { canvas: texCanvasRef.current, ctx: texCtxRef.current, tex: threeTexRef.current };
  }, []);

  const paintAt = useCallback((uvX, uvY, faceKey) => {
    const { canvas: c, ctx, tex } = getCanvas(faceKey);
    if (!c || !tex) return;
    const [r, g, b, a] = color;
    const px = Math.max(0, Math.min(canvasSize - 1, Math.round(uvX * canvasSize - 0.5)));
    const py = Math.max(0, Math.min(canvasSize - 1, Math.round((1 - uvY) * canvasSize - 0.5)));
    const radius = Math.max(0, brushSize - 1);
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (dx*dx + dy*dy <= radius*radius + radius) {
          const x = px + dx, y = py + dy;
          if (x < 0 || x >= canvasSize || y < 0 || y >= canvasSize) continue;
          if (tool === 'pencil') {
            ctx.globalAlpha = a / 255;
            ctx.fillStyle = `rgb(${r},${g},${b})`;
            ctx.fillRect(x, y, 1, 1);
            ctx.globalAlpha = 1;
          } else if (tool === 'eraser') {
            ctx.clearRect(x, y, 1, 1);
          }
        }
      }
    }
    tex.needsUpdate = true;
    didPaintRef.current = true;
    paintRef.current.lastFaceKey = faceKey || null;
    if (onPaint) onPaint(ctx.getImageData(0, 0, canvasSize, canvasSize), faceKey);
  }, [tool, color, brushSize, canvasSize, onPaint, getCanvas]);

  const floodFillAt = useCallback((uvX, uvY, faceKey) => {
    const { canvas: c, ctx, tex } = getCanvas(faceKey);
    if (!c || !tex) return;
    const px = Math.max(0, Math.min(canvasSize - 1, Math.round(uvX * canvasSize - 0.5)));
    const py = Math.max(0, Math.min(canvasSize - 1, Math.round((1 - uvY) * canvasSize - 0.5)));
    const imgData = ctx.getImageData(0, 0, canvasSize, canvasSize);
    const data = imgData.data;
    const tidx = (py * canvasSize + px) * 4;
    const tr = data[tidx], tg = data[tidx+1], tb = data[tidx+2], ta = data[tidx+3];
    const [fr, fg, fb, fa] = color;
    if (tr===fr && tg===fg && tb===fb && ta===fa) return;
    const stack = [[px, py]];
    const visited = new Uint8Array(canvasSize * canvasSize);
    while (stack.length > 0) {
      const [x, y] = stack.pop();
      if (x<0||x>=canvasSize||y<0||y>=canvasSize) continue;
      const i = y * canvasSize + x;
      if (visited[i]) continue;
      const pi = i * 4;
      if (data[pi]!==tr||data[pi+1]!==tg||data[pi+2]!==tb||data[pi+3]!==ta) continue;
      visited[i] = 1;
      data[pi]=fr; data[pi+1]=fg; data[pi+2]=fb; data[pi+3]=fa;
      stack.push([x+1,y],[x-1,y],[x,y+1],[x,y-1]);
    }
    ctx.putImageData(imgData, 0, 0);
    tex.needsUpdate = true;
    didPaintRef.current = true;
    paintRef.current.lastFaceKey = faceKey || null;
    if (onPaint) onPaint(ctx.getImageData(0, 0, canvasSize, canvasSize), faceKey);
  }, [tool, color, canvasSize, onPaint, getCanvas]);

  const pickColorAt = useCallback((uvX, uvY, faceKey) => {
    const { canvas: c, ctx } = getCanvas(faceKey);
    if (!c || !onColorPick) return;
    const px = Math.max(0, Math.min(canvasSize - 1, Math.round(uvX * canvasSize - 0.5)));
    const py = Math.max(0, Math.min(canvasSize - 1, Math.round((1 - uvY) * canvasSize - 0.5)));
    const p = ctx.getImageData(px, py, 1, 1).data;
    onColorPick([p[0], p[1], p[2], p[3]]);
  }, [canvasSize, onColorPick, getCanvas]);

  const doRaycast = useCallback((clientX, clientY) => {
    if (!mountRef.current || !groupRef.current || !camRef.current) return null;
    const rect = mountRef.current.getBoundingClientRect();
    mouseRef.current.x = ((clientX - rect.left)  / rect.width)  * 2 - 1;
    mouseRef.current.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    raycasterRef.current.setFromCamera(mouseRef.current, camRef.current);
    const hits = raycasterRef.current.intersectObject(groupRef.current, true);
    if (hits.length === 0) return null;
    const hit = hits[0];
    // faceKey aus faceIndex bestimmen (BoxGeometry: 2 Dreiecke pro Face)
    const faceKey = FACE_KEYS[Math.floor(hit.faceIndex / 2)] || null;
    return { ...hit, faceKey };
  }, []);

  // ── Event-Handler ────────────────────────────────────────────────────────
  const handlePointerDown = useCallback((e) => {
    if (e.button === 0) {
      if (e.shiftKey || tool === 'none') {
        orbitRef.current = { active: true, lastX: e.clientX, lastY: e.clientY };
      } else {
        paintRef.current.active = true;
        didPaintRef.current = false;
        const hit = doRaycast(e.clientX, e.clientY);
        if (hit?.uv) {
          const fk = hit.faceKey;
          if (tool === 'fill') floodFillAt(hit.uv.x, hit.uv.y, fk);
          else if (tool === 'picker') pickColorAt(hit.uv.x, hit.uv.y, fk);
          else paintAt(hit.uv.x, hit.uv.y, fk);
        }
      }
    } else if (e.button === 2) {
      orbitRef.current = { active: true, lastX: e.clientX, lastY: e.clientY };
    }
  }, [tool, doRaycast, paintAt, floodFillAt, pickColorAt]);

  const handlePointerMove = useCallback((e) => {
    if (orbitRef.current.active) {
      const dx = e.clientX - orbitRef.current.lastX;
      const dy = e.clientY - orbitRef.current.lastY;
      orbitRef.current.lastX = e.clientX;
      orbitRef.current.lastY = e.clientY;
      rotRef.current.y += dx * 0.012;
      rotRef.current.x += dy * 0.012;
      rotRef.current.x = Math.max(-Math.PI/2, Math.min(Math.PI/2, rotRef.current.x));
      if (groupRef.current) {
        groupRef.current.rotation.y = rotRef.current.y;
        groupRef.current.rotation.x = rotRef.current.x;
      }
    } else if (paintRef.current.active) {
      const hit = doRaycast(e.clientX, e.clientY);
      if (hit?.uv && tool !== 'fill' && tool !== 'picker') {
        paintAt(hit.uv.x, hit.uv.y, hit.faceKey);
      }
    }
  }, [doRaycast, paintAt, tool]);

  const handlePointerUp = useCallback(() => {
    orbitRef.current.active = false;
    const wasPainting = paintRef.current.active;
    paintRef.current.active = false;
    if (wasPainting && didPaintRef.current && onStrokeEnd) {
      const faceKey = paintRef.current.lastFaceKey;
      if (faceKey && facesRef.current[faceKey]) {
        const { ctx } = facesRef.current[faceKey];
        onStrokeEnd(ctx.getImageData(0, 0, canvasSize, canvasSize), faceKey);
      } else if (texCtxRef.current) {
        onStrokeEnd(texCtxRef.current.getImageData(0, 0, canvasSize, canvasSize), null);
      }
    }
    didPaintRef.current = false;
  }, [onStrokeEnd, canvasSize]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    zoomRef.current = Math.max(0.5, Math.min(4.0, zoomRef.current + e.deltaY * 0.002));
    if (camRef.current) camRef.current.position.z = zoomRef.current;
  }, []);

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    const preventCtx = e => e.preventDefault();
    el.addEventListener('pointerdown', handlePointerDown);
    el.addEventListener('pointermove', handlePointerMove);
    el.addEventListener('pointerup',   handlePointerUp);
    el.addEventListener('pointerleave', handlePointerUp);
    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('contextmenu', preventCtx);
    return () => {
      el.removeEventListener('pointerdown', handlePointerDown);
      el.removeEventListener('pointermove', handlePointerMove);
      el.removeEventListener('pointerup',   handlePointerUp);
      el.removeEventListener('pointerleave', handlePointerUp);
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('contextmenu', preventCtx);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp, handleWheel]);

  // ── Imperative Handle ────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    // Single-Textur API (abwaertskompatibel)
    getImageData: () => {
      const c = texCanvasRef.current;
      if (!c) return null;
      const ctx = texCtxRef.current || c.getContext('2d', { willReadFrequently: true });
      return ctx.getImageData(0, 0, canvasSize, canvasSize);
    },
    setImageData: (imgData) => {
      const c = texCanvasRef.current;
      if (!c) return;
      const ctx = texCtxRef.current || c.getContext('2d', { willReadFrequently: true });
      ctx.putImageData(imgData, 0, 0);
      if (threeTexRef.current) threeTexRef.current.needsUpdate = true;
    },
    getDataUrl: () => texCanvasRef.current?.toDataURL('image/png'),
    loadFromDataUrl: (dataUrl) => {
      const c = texCanvasRef.current;
      if (!c) return;
      const img = new Image();
      img.onload = () => {
        const ctx = texCtxRef.current || c.getContext('2d', { willReadFrequently: true });
        ctx.clearRect(0, 0, canvasSize, canvasSize);
        ctx.drawImage(img, 0, 0, canvasSize, canvasSize);
        if (threeTexRef.current) threeTexRef.current.needsUpdate = true;
      };
      img.src = dataUrl;
    },
    clear: () => {
      const c = texCanvasRef.current;
      if (!c) return;
      const ctx = texCtxRef.current || c.getContext('2d', { willReadFrequently: true });
      ctx.clearRect(0, 0, canvasSize, canvasSize);
      if (threeTexRef.current) threeTexRef.current.needsUpdate = true;
    },
    // Multi-Textur API
    setFaceImageData: (faceKey, imgData) => {
      const face = facesRef.current[faceKey];
      if (!face) return;
      face.ctx.putImageData(imgData, 0, 0);
      face.tex.needsUpdate = true;
    },
    getFaceImageData: (faceKey) => {
      const face = facesRef.current[faceKey];
      if (!face) return null;
      return face.ctx.getImageData(0, 0, canvasSize, canvasSize);
    },
    getFaceDataUrl: (faceKey) => {
      const face = facesRef.current[faceKey];
      return face?.canvas?.toDataURL('image/png') || null;
    },
    getAllFaceDataUrls: () => {
      const result = {};
      for (const [k, face] of Object.entries(facesRef.current)) {
        result[k] = face.canvas.toDataURL('image/png');
      }
      return result;
    },
  }), [canvasSize]);

  const cursor =
    tool === 'picker' ? 'crosshair' :
    tool === 'eraser' ? 'cell' :
    tool === 'fill'   ? 'copy' : 'crosshair';

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div
        ref={mountRef}
        style={{ width: '100%', height: '100%', cursor, userSelect: 'none', touchAction: 'none' }}
      />
      {isAnimated && animFrameCount > 1 && (
        <div style={{
          position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(0,0,0,0.75)', borderRadius: 6, padding: '4px 10px',
        }}>
          <button
            onClick={() => setAnimPlaying(p => !p)}
            style={{
              background: animPlaying ? '#ef4444' : '#10b981',
              color: '#fff', border: 'none', borderRadius: 4,
              padding: '3px 10px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
            }}
          >
            {animPlaying ? '⏸ Pause' : '▶ Play'}
          </button>
          <label style={{ color: '#9ca3af', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            FPS
            <input
              type="range" min={1} max={30} value={animFps}
              onChange={e => setAnimFps(Number(e.target.value))}
              style={{ width: 60, cursor: 'pointer' }}
            />
            <span style={{ color: '#d1d5db', minWidth: 18, textAlign: 'right' }}>{animFps}</span>
          </label>
        </div>
      )}
    </div>
  );
});

Texture3DPainter.displayName = 'Texture3DPainter';
export default Texture3DPainter;
